import {
  DEFAULT_SESSION_SETTINGS,
  type AccessType,
  type AuthUser,
  type DeviceOrganization,
  type HostInputMessage,
  type IceServer,
  type LoginResponse,
  type MeResponse,
  type OrganizationRef,
  type OrganizationSummary,
  type ScreenMonitor,
  type ServerMessage,
  type SessionEndReason,
  type SessionRole,
  type SessionSettings,
  type SignalPayload,
  type UserRef,
  type ViewerInputMessage,
} from '@viewer/protocol';
import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import { ApiError, api } from '../api/client';
import { authToken, loadAuthToken, saveAuthToken } from '../auth/token';
import { browserIdentity } from '../browser';
import { FORCE_RELAY, INSIDE_APP } from '../config';
import { clipboardPullFail, clipboardRelease } from '../tauri/clipboard';
import { AgentTransport, type SignalingTransport } from '../signaling/agent-transport';
import { SignalingClient, type SignalingStatus } from '../signaling/client';
import {
  agentSetPassword,
  agentSetUserToken,
  onAgentHostSessions,
  onAgentPassword,
  serviceRunning,
} from '../tauri/agent';
import {
  accessPasswordEnabled,
  accessPasswordSet,
  accessPasswordVerify,
  identityLoad,
  identitySave,
  windowBringToFront,
} from '../tauri/commands';
import {
  hostAddIceCandidate,
  hostSetAnswer,
  hostStart,
  hostStop,
  onHostIce,
  onHostState,
  onTrayEndSessions,
  traySetConnections,
  type HostConnection,
  type PeerState,
} from '../tauri/host';
import { ViewerPeer, type LinkSummary } from '../webrtc/viewer-peer';
import { SessionTransfer } from '../../features/viewer/sessionTransfer';
import { cursorImage, type RemoteCursorImage, type RemoteCursorLook } from '../../features/viewer/remoteCursor';

export interface IncomingRequest {
  sessionId: string;
  viewerId: string;
  viewerName: string;
  expiresAt: string;
  /** Colaborador logado que pediu (dispositivo de empresa). */
  viewerUser: UserRef | null;
  /** O que a empresa concedeu; `control` no modo avulso. */
  access: AccessType;
  /** Empresa pela qual o colaborador pede o acesso. */
  organization: OrganizationRef | null;
  /** Acesso sem supervisão: sem resposta até este horário, a conexão começa sozinha. */
  autoAcceptAt: string | null;
}

export interface OutgoingRequest {
  /** Só existe depois que o servidor confirma o pedido. */
  sessionId: string | null;
  targetId: string;
  /** O computador remoto aceita senha de acesso no lugar do aceite manual. */
  passwordEnabled: boolean;
  /** Preenchido depois de uma senha errada; 0 = tentativas esgotadas. */
  attemptsLeft: number | null;
  checkingPassword: boolean;
  /** Acesso sem supervisão: a conexão começa sozinha neste horário se ninguém recusar. */
  autoAcceptAt: string | null;
}

export interface SessionInfo {
  sessionId: string;
  role: SessionRole;
  token: string;
  peerId: string;
  peerName: string;
  iceServers: IceServer[];
  expiresAt: string;
  /** `view`: só vídeo, nenhuma entrada é enviada nem aplicada. */
  access: AccessType;
  /** Ausente na mensagem (modo avulso) equivale a liberado. */
  clipboard: boolean;
  fileTransfer: boolean;
  /** Só no Host, pelo caminho da empresa: quem pediu e por qual empresa. */
  viewerUser: UserRef | null;
  organization: OrganizationRef | null;
}

export interface ActiveSession {
  info: SessionInfo;
  peerState: PeerState;
  inputOpen: boolean;
  /** Só no Viewer: vídeo recebido do Host. */
  remoteStream: MediaStream | null;
  /** Só no Viewer: tamanho do frame e da área capturada, e onde fica cada monitor no frame. */
  screen: {
    frameWidth: number;
    frameHeight: number;
    monitorWidth: number;
    monitorHeight: number;
    monitors: ScreenMonitor[];
  } | null;
  /** Só no Viewer: latência medida por ping/pong no DataChannel (ms). */
  rttMs: number | null;
  /** Só no Viewer: caminho ICE em uso (direto ou via TURN). */
  link: LinkSummary | null;
  /** Escolhidas pelo Viewer para esta conexão; no Host, o que o Viewer pediu e já foi aplicado. */
  settings: SessionSettings;
  /** Só no Viewer: volume do som recebido (0 a 1). Fica neste lado; o Host não sabe dele. */
  volume: number;
  /** Só no Viewer: forma do cursor do Host, que o ponteiro local imita. */
  remoteCursor: RemoteCursorLook | null;
  /** A pessoa do Host está usando o mouse físico; quem acessa só aponta com a seta verde (modo guia). */
  mouseBlocked: boolean;
  /** Só no Viewer: mouse e teclado físicos do Host bloqueados (Modo Bloqueio); `null` enquanto o Host não responde ao pedido. */
  inputBlocked: boolean | null;
  /** Arquivos copiados no computador remoto, ainda não baixados. */
  remoteFiles: { count: number; skippedFolders: number } | null;
  /** Transferência de arquivos em andamento, neste lado. */
  transfer: { label: string; percent: number } | null;
}

export interface AppState {
  connection: SignalingStatus;
  deviceId: string | null;
  /** Este computador tem senha de acesso configurada. */
  accessPassword: boolean;
  /** Colaborador logado nesta máquina/navegador. */
  user: AuthUser | null;
  /** Empresas de que o usuário participa. */
  organizations: OrganizationSummary[];
  /** Empresas que gerenciam este computador (só no app); vazio no modo avulso. */
  deviceOrganizations: DeviceOrganization[];
  /** Vinculação em andamento. */
  enrolling: boolean;
  notice: { kind: 'error' | 'info'; text: string } | null;
  incoming: IncomingRequest | null;
  outgoing: OutgoingRequest | null;
  /** Sessão em que este lado é o Viewer. */
  session: ActiveSession | null;
  /** Pessoas acessando este computador agora; várias ao mesmo tempo. */
  hostSessions: HostConnection[];
}

type Listener = () => void;
/** `null`: cursor escondido ou fora da tela capturada. */
export type RemoteCursorListener = (position: { x: number; y: number } | null) => void;

const endReasonText: Record<SessionEndReason, string> = {
  host_ended: 'O computador remoto encerrou a sessão.',
  viewer_ended: 'Sessão encerrada.',
  host_offline: 'O computador remoto ficou indisponível.',
  viewer_offline: 'O outro computador ficou indisponível.',
  expired: 'A sessão expirou.',
  server_shutdown: 'O servidor foi encerrado.',
  access_revoked: 'A empresa retirou a permissão de acesso. A sessão foi encerrada.',
};
const CONNECTION_FAILED_TEXT = 'A conexão com o computador remoto falhou. Tente conectar de novo.';

const PING_INTERVAL_MS = 2000;
const PASSWORD_ATTEMPTS_PER_REQUEST = 3;
/** Muitos erros seguidos (de qualquer pedido) bloqueiam a senha por um tempo. */
const PASSWORD_LOCKOUT_FAILURES = 10;
const PASSWORD_LOCKOUT_WINDOW_MS = 15 * 60_000;

/**
 * Estado global da aplicação e orquestração entre signaling, Host (Rust) e Viewer (WebRTC).
 * Exposto ao React via `useSyncExternalStore`.
 */
export class SessionController {
  private state: AppState = {
    connection: 'offline',
    deviceId: null,
    accessPassword: false,
    user: null,
    organizations: [],
    deviceOrganizations: [],
    enrolling: false,
    notice: null,
    incoming: null,
    outgoing: null,
    session: null,
    hostSessions: [],
  };
  private readonly listeners = new Set<Listener>();
  private signaling: SignalingTransport | null = null;
  /** Serviço ViewerHost no ar: a sinalização e o Host ficam no agente. */
  private agentMode = false;
  private agentUnlisten: UnlistenFn[] = [];
  private epoch = 0;
  private viewerPeer: ViewerPeer | null = null;
  private fileTransfer: SessionTransfer | null = null;
  private cursorImages = new Map<number, RemoteCursorImage>();
  private cursorPosition: { x: number; y: number } | null = null;
  private readonly cursorListeners = new Set<RemoteCursorListener>();
  private clipboardBound = false;
  private trayBound = false;
  /** Sem o serviço, as sessões Host vivem neste processo; aqui fica o token de cada uma. */
  private hostInfos = new Map<string, SessionInfo>();
  private hostUnlisten: UnlistenFn[] = [];
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  /** Tentativas de senha no pedido recebido atual. */
  private passwordAttempts = 0;
  private passwordFailures: number[] = [];

  start(signalingUrl: string, deviceName: string) {
    this.bindClipboardPull();
    this.bindTray();
    const epoch = ++this.epoch;
    if (!INSIDE_APP) {
      this.startDirect(signalingUrl, deviceName);
      return;
    }
    void serviceRunning()
      .then((running) => {
        if (epoch !== this.epoch) return;
        if (running) this.startAgent(epoch);
        else this.startDirect(signalingUrl, deviceName);
      })
      .catch((err) => console.error('falha ao consultar o serviço', err));
  }

  stop() {
    this.epoch++;
    // Sem o WebSocket deste processo, fechar a janela não derruba a sessão sozinho.
    if (this.agentMode) {
      const sessionId =
        this.state.session?.info.role === 'viewer'
          ? this.state.session.info.sessionId
          : this.state.outgoing?.sessionId;
      if (sessionId) this.signaling?.send({ type: 'session_end', sessionId });
    }
    this.signaling?.close();
    this.signaling = null;
    for (const unlisten of this.hostUnlisten) unlisten();
    this.hostUnlisten = [];
    for (const unlisten of this.agentUnlisten) unlisten();
    this.agentUnlisten = [];
    void this.teardownSession();
    this.dropHostSessions([...this.hostInfos.keys()]);
    this.agentMode = false;
  }

  /** Sem serviço: WebSocket e Host neste processo, como antes. */
  private startDirect(signalingUrl: string, deviceName: string) {
    this.agentMode = false;
    this.signaling = new SignalingClient({
      url: signalingUrl,
      deviceName,
      canHost: INSIDE_APP,
      identity: INSIDE_APP ? { load: identityLoad, save: identitySave } : browserIdentity,
      userToken: authToken,
      onStatus: (connection) => this.onSignalingStatus(connection),
      onDeviceId: (deviceId) => this.update({ deviceId }),
      onMessage: (message) => this.onServerMessage(message),
      log: (msg, ...rest) => console.info(msg, ...rest),
    });
    // O token precisa estar em memória antes do primeiro hello.
    const signaling = this.signaling;
    void loadAuthToken()
      .catch((err) => {
        console.error('falha ao ler o login', err);
        return null;
      })
      .then((token) => {
        if (this.signaling !== signaling) return;
        signaling.connect();
        if (token) void this.refreshMe();
      });
    if (INSIDE_APP) {
      void this.listenHostEvents();
      void accessPasswordEnabled()
        .then((accessPassword) => {
          this.update({ accessPassword });
          this.sendHostOptions();
        })
        .catch((err) => console.error('falha ao ler a senha de acesso', err));
    }
  }

  /** Com serviço: o agente é dono do WebSocket e do Host. */
  private startAgent(epoch: number) {
    this.agentMode = true;
    const signaling = new AgentTransport({
      onStatus: (connection) => this.onSignalingStatus(connection),
      onDeviceId: (deviceId) => this.update({ deviceId }),
      onMessage: (message) => this.onServerMessage(message),
      log: (msg, ...rest) => console.info(msg, ...rest),
    });
    this.signaling = signaling;
    void this.bootAgent(epoch, signaling);
  }

  private async bootAgent(epoch: number, signaling: AgentTransport) {
    const token = await loadAuthToken().catch((err) => {
      console.error('falha ao ler o login', err);
      return null;
    });
    if (epoch !== this.epoch || this.signaling !== signaling) return;
    try {
      await Promise.all([signaling.connect(), this.listenAgentPassword(epoch), this.listenAgentHostSessions(epoch)]);
    } catch (err) {
      if (epoch !== this.epoch) return;
      console.error('falha ao conectar pelo agente', err);
      return;
    }
    if (epoch !== this.epoch || this.signaling !== signaling) return;
    try {
      await agentSetUserToken(authToken());
    } catch (err) {
      console.error('falha ao enviar o login ao agente', err);
    }
    if (token) void this.refreshMe();
  }

  private async listenAgentPassword(epoch: number) {
    const unlisten = await onAgentPassword((enabled) => this.update({ accessPassword: enabled }));
    if (epoch !== this.epoch) {
      unlisten();
      return;
    }
    this.agentUnlisten.push(unlisten);
  }

  /** Com o serviço, quem conhece as sessões Host é o agente; a lista chega pronta. */
  private async listenAgentHostSessions(epoch: number) {
    const unlisten = await onAgentHostSessions((hostSessions) => this.update({ hostSessions }));
    if (epoch !== this.epoch) {
      unlisten();
      return;
    }
    this.agentUnlisten.push(unlisten);
  }

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.state;

  /** Posição do cursor do Host. Fica fora do estado para a tela não redesenhar a cada movimento. */
  watchRemoteCursor = (listener: RemoteCursorListener) => {
    this.cursorListeners.add(listener);
    listener(this.cursorPosition);
    return () => {
      this.cursorListeners.delete(listener);
    };
  };

  dismissNotice() {
    this.update({ notice: null });
  }

  /* ---------------- conta ---------------- */

  async login(email: string, password: string) {
    const res = await api.post<LoginResponse>('/api/auth/login', { email, password });
    await this.signedIn(res);
  }

  async signup(name: string, email: string, password: string) {
    const res = await api.post<LoginResponse>('/api/auth/signup', { name, email, password });
    await this.signedIn(res);
  }

  async logout() {
    await api.post('/api/auth/logout').catch(() => {});
    await saveAuthToken(null);
    this.update({ user: null, organizations: [] });
    this.syncAccount();
  }

  /** Recarrega usuário e empresas (depois de criar empresa, aceitar convite etc.). */
  async refreshMe() {
    try {
      const me = await api.get<MeResponse>('/api/me');
      this.update({ user: me.user, organizations: me.organizations });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Token vencido ou revogado.
        await saveAuthToken(null);
        this.update({ user: null, organizations: [] });
        if (this.agentMode) this.pushUserToken();
      } else {
        console.error('falha ao carregar a conta', err);
      }
    }
  }

  /** Atualiza o usuário no estado após editar o perfil. */
  applyUser(user: AuthUser) {
    this.update({ user });
  }

  private async signedIn(res: LoginResponse) {
    await saveAuthToken(res.token);
    this.update({ user: res.user });
    await this.refreshMe();
    // Refaz o hello para o servidor associar esta conexão ao usuário.
    this.syncAccount();
  }

  /** Entra ou sai da conta na sinalização. No modo agente, quem refaz o hello é o serviço. */
  private syncAccount() {
    if (this.agentMode) {
      this.pushUserToken();
      return;
    }
    this.signaling?.reconnect();
  }

  private pushUserToken() {
    void agentSetUserToken(authToken()).catch((err) => console.error('falha ao enviar o login ao agente', err));
  }

  /** Host: vincula este computador a uma empresa com o código gerado no painel. */
  enrollDevice(code: string) {
    if (!INSIDE_APP || this.state.enrolling) return;
    if (this.signaling?.send({ type: 'enroll_device', code })) this.update({ enrolling: true, notice: null });
  }

  /** Host: tira a empresa deste computador; ela só volta com um novo código de vinculação. */
  leaveOrganization(organizationId: string) {
    if (!INSIDE_APP) return;
    this.signaling?.send({ type: 'leave_organization', organizationId });
  }

  /** Host: libera ou retira o acesso sem supervisão de uma empresa neste computador. */
  setUnattended(organizationId: string, allowed: boolean) {
    if (!INSIDE_APP) return;
    this.signaling?.send({ type: 'set_unattended', organizationId, allowed });
  }

  /* ---------------- ações do usuário ---------------- */

  connectTo(targetId: string) {
    if (this.state.session || this.state.outgoing) return;
    this.update({
      outgoing: {
        sessionId: null,
        targetId,
        passwordEnabled: false,
        attemptsLeft: null,
        checkingPassword: false,
        autoAcceptAt: null,
      },
      notice: null,
    });
    this.signaling?.send({ type: 'session_request', targetId });
  }

  /** Viewer: tenta entrar com a senha de acesso em vez de esperar o aceite. */
  submitPassword(password: string) {
    const outgoing = this.state.outgoing;
    if (!outgoing?.sessionId || !outgoing.passwordEnabled || outgoing.attemptsLeft === 0 || outgoing.checkingPassword) {
      return;
    }
    if (this.signaling?.send({ type: 'session_password', sessionId: outgoing.sessionId, password })) {
      this.update({ outgoing: { ...outgoing, checkingPassword: true } });
    }
  }

  /** Host: define (ou remove, com `null`) a senha de acesso deste computador. */
  async setAccessPassword(password: string | null) {
    if (this.agentMode) {
      await agentSetPassword(password);
      return;
    }
    await accessPasswordSet(password);
    this.update({ accessPassword: password !== null });
    this.sendHostOptions();
  }

  cancelOutgoing() {
    const outgoing = this.state.outgoing;
    if (!outgoing) return;
    if (outgoing.sessionId) this.signaling?.send({ type: 'session_end', sessionId: outgoing.sessionId });
    this.update({ outgoing: null });
  }

  acceptIncoming(via?: 'password') {
    const incoming = this.state.incoming;
    if (!incoming) return;
    this.signaling?.send({ type: 'session_accept', sessionId: incoming.sessionId, ...(via ? { via } : {}) });
    this.update({ incoming: null });
  }

  rejectIncoming() {
    const incoming = this.state.incoming;
    if (!incoming) return;
    this.signaling?.send({ type: 'session_reject', sessionId: incoming.sessionId });
    this.update({ incoming: null });
  }

  endSession() {
    this.closeSession({ kind: 'info', text: endReasonText.viewer_ended });
  }

  /** Host: desconecta só estas pessoas (uma, uma empresa ou todas); as outras continuam. */
  endHostSessions(sessionIds: string[]) {
    for (const sessionId of sessionIds) this.signaling?.send({ type: 'session_end', sessionId });
    // Com o serviço, o agente para a transmissão ao receber o `session_ended` e manda a lista nova.
    this.dropHostSessions(sessionIds);
  }

  /** Para as sessões Host deste processo sem esperar o servidor. */
  private dropHostSessions(sessionIds: string[]) {
    const ending = sessionIds.filter((sessionId) => this.hostInfos.delete(sessionId));
    if (ending.length === 0) return;
    for (const sessionId of ending) {
      void hostStop(sessionId).catch((err) => console.warn('falha ao parar host', err));
    }
    this.update({ hostSessions: this.state.hostSessions.filter((s) => !ending.includes(s.sessionId)) });
  }

  private bindTray() {
    if (!INSIDE_APP || this.trayBound) return;
    this.trayBound = true;
    void onTrayEndSessions((sessionIds) => this.endHostSessions(sessionIds));
  }

  private closeSession(notice: NonNullable<AppState['notice']>) {
    const session = this.state.session;
    if (!session) return;
    this.signaling?.send({ type: 'session_end', sessionId: session.info.sessionId });
    // Não espera o servidor: derruba a conexão imediatamente.
    void this.teardownSession();
    this.update({ session: null, notice });
  }

  /** `failed` é definitivo no WebRTC: sem encerrar, os dois lados ficariam esperando para sempre. */
  private onPeerState(peerState: PeerState) {
    this.patchSession({ peerState });
    if (peerState === 'failed') this.closeSession({ kind: 'error', text: CONNECTION_FAILED_TEXT });
  }

  /** O Explorer pediu os bytes de um arquivo virtual. */
  pullRemoteFile(clipboardId: string, index: number, transferId: string) {
    if (!this.fileTransfer) {
      void clipboardPullFail(transferId);
      return;
    }
    this.fileTransfer.pullRemote(clipboardId, index, transferId);
  }

  private bindClipboardPull() {
    if (!INSIDE_APP || this.clipboardBound) return;
    this.clipboardBound = true;
    void listen<{ clipboardId: string; index: number; transferId: string }>('clipboard:pull', (event) => {
      this.pullRemoteFile(event.payload.clipboardId, event.payload.index, event.payload.transferId);
    });
    void listen<{ transferId: string }>('clipboard:pull-cancel', (event) => {
      this.fileTransfer?.cancelPull(event.payload.transferId);
    });
  }

  /** Viewer -> Host, pelo DataChannel. Em sessão só de visualização nada é enviado. */
  sendInput(message: ViewerInputMessage): boolean {
    if (this.state.session?.info.access !== 'control') return false;
    return this.viewerPeer?.sendInput(message) ?? false;
  }

  /** Ctrl+V no app: lê a área de transferência local e manda colar no remoto. */
  pasteLocalClipboard() {
    this.fileTransfer?.pasteLocal();
  }

  /** Ctrl+V no navegador. `false` se o evento veio vazio. */
  pasteFromBrowser(data: DataTransfer): boolean {
    return this.fileTransfer?.pasteData(data) ?? false;
  }

  /** Enviar arquivos ou soltar na tela: vão para a Área de Trabalho remota. */
  sendFiles(files: File[]) {
    this.fileTransfer?.sendFiles(files);
  }

  sendDroppedFiles(data: DataTransfer) {
    this.fileTransfer?.sendDropped(data);
  }

  downloadCopiedFiles() {
    this.fileTransfer?.downloadCopied();
  }

  cancelTransfer() {
    this.fileTransfer?.cancel();
  }

  /** Viewer: muda as configurações só desta conexão. Com o canal fechado, vão quando ele abrir. */
  updateSessionSettings(patch: Partial<SessionSettings>) {
    const session = this.state.session;
    if (session?.info.role !== 'viewer') return;
    const settings = { ...session.settings, ...patch };
    const blockChanged = settings.blockLocalInput !== session.settings.blockLocalInput;
    this.patchSession({ settings, inputBlocked: blockChanged ? null : session.inputBlocked });
    this.sendSessionSettings();
  }

  setSessionVolume(volume: number) {
    if (this.state.session?.info.role !== 'viewer') return;
    this.patchSession({ volume: Math.min(1, Math.max(0, volume)) });
  }

  /** Estatísticas brutas do WebRTC do Viewer (diagnóstico). */
  getViewerStats(): Promise<RTCStatsReport> | null {
    return this.viewerPeer?.getStats() ?? null;
  }

  /* ---------------- signaling ---------------- */

  private onSignalingStatus(connection: SignalingStatus) {
    this.update({ connection });
    if (connection === 'online') this.sendHostOptions();
    if (
      connection !== 'online' &&
      (this.state.session || this.state.incoming || this.state.outgoing || this.hostInfos.size > 0)
    ) {
      // Sem servidor não há como continuar a negociação nem encerrar de forma limpa.
      void this.teardownSession();
      this.dropHostSessions([...this.hostInfos.keys()]);
      this.update({
        session: null,
        incoming: null,
        outgoing: null,
        notice: { kind: 'error', text: 'Conexão com o servidor perdida. A sessão foi encerrada.' },
      });
    }
  }

  private onServerMessage(message: ServerMessage) {
    switch (message.type) {
      case 'welcome':
        this.update({ deviceOrganizations: message.organizations ?? [] });
        if (authToken() && !message.user) {
          // O servidor não reconheceu o token: a sessão de login venceu.
          void saveAuthToken(null);
          this.update({ user: null, organizations: [] });
          if (this.agentMode) this.pushUserToken();
        }
        return;

      case 'device_organizations': {
        const before = this.state.deviceOrganizations;
        const added = message.organizations.find((o) => !before.some((b) => b.id === o.id));
        const removed = before.find((b) => !message.organizations.some((o) => o.id === b.id));
        let notice = this.state.notice;
        if (added) notice = { kind: 'info', text: `Este computador agora é gerenciado por ${added.name}.` };
        else if (removed) notice = { kind: 'info', text: `${removed.name} não gerencia mais este computador.` };
        this.update({ deviceOrganizations: message.organizations, enrolling: false, notice });
        return;
      }

      case 'session_incoming': {
        this.passwordAttempts = 0;
        // Os prazos vêm no relógio do servidor; o deste computador pode estar adiantado ou atrasado.
        const skew = message.sentAt ? Date.now() - new Date(message.sentAt).getTime() : 0;
        const toLocal = (at: string) => new Date(new Date(at).getTime() + skew).toISOString();
        this.update({
          incoming: {
            sessionId: message.sessionId,
            viewerId: message.viewerId,
            viewerName: message.viewerName,
            expiresAt: toLocal(message.expiresAt),
            viewerUser: message.viewerUser ?? null,
            access: message.access ?? 'control',
            organization: message.organization ?? null,
            autoAcceptAt: message.autoAcceptAt ? toLocal(message.autoAcceptAt) : null,
          },
        });
        void windowBringToFront().catch(() => undefined);
        return;
      }

      case 'session_pending':
        if (this.state.outgoing) {
          this.update({
            outgoing: {
              ...this.state.outgoing,
              sessionId: message.sessionId,
              passwordEnabled: message.passwordEnabled,
              autoAcceptAt: message.autoAcceptAt ?? null,
            },
          });
        }
        return;

      case 'session_password':
        if (!this.agentMode) void this.onPasswordAttempt(message.sessionId, message.password);
        return;

      case 'session_password_rejected': {
        const outgoing = this.state.outgoing;
        if (outgoing?.sessionId !== message.sessionId) return;
        this.update({ outgoing: { ...outgoing, attemptsLeft: message.attemptsLeft, checkingPassword: false } });
        return;
      }

      case 'session_started':
        void this.onSessionStarted({
          sessionId: message.sessionId,
          role: message.role,
          token: message.token,
          peerId: message.peerId,
          peerName: message.peerName,
          iceServers: message.iceServers,
          expiresAt: message.expiresAt,
          access: message.access ?? 'control',
          clipboard: message.clipboard ?? true,
          fileTransfer: message.fileTransfer ?? true,
          viewerUser: message.viewerUser ?? null,
          organization: message.organization ?? null,
        });
        return;

      case 'session_rejected':
        this.update({ outgoing: null, notice: { kind: 'info', text: 'O computador remoto recusou a conexão.' } });
        return;

      case 'session_ended': {
        if (this.hostInfos.has(message.sessionId)) {
          this.dropHostSessions([message.sessionId]);
          return;
        }
        const current = this.state.session?.info.sessionId ?? this.state.outgoing?.sessionId;
        if (current !== message.sessionId && this.state.incoming?.sessionId !== message.sessionId) return;
        // O Host desiste sozinho quando a conexão falha; daqui só se vê "encerrou".
        const failed = message.reason === 'host_ended' && this.state.session && this.state.session.peerState !== 'connected';
        void this.teardownSession();
        this.update({
          session: null,
          outgoing: null,
          incoming: null,
          notice: failed
            ? { kind: 'error', text: CONNECTION_FAILED_TEXT }
            : { kind: 'info', text: endReasonText[message.reason] },
        });
        return;
      }

      case 'signal':
        void this.onSignal(message.sessionId, message.payload);
        return;

      case 'error':
        if (message.code === 'replaced') {
          // A conexão vai ser fechada em seguida; encerra antes para o aviso não ser trocado pelo de conexão perdida.
          void this.teardownSession();
          this.dropHostSessions([...this.hostInfos.keys()]);
          this.update({ session: null, incoming: null, outgoing: null });
        }
        if (message.ref === 'session_request') this.update({ outgoing: null });
        if (message.ref === 'enroll_device') this.update({ enrolling: false });
        this.update({ notice: { kind: 'error', text: message.message } });
        return;

      default:
        return;
    }
  }

  private sendHostOptions() {
    if (this.agentMode || !INSIDE_APP) return;
    this.signaling?.send({ type: 'host_options', passwordEnabled: this.state.accessPassword });
  }

  /** Host: confere a senha enviada pelo Viewer. Certa, aceita como se o usuário tivesse clicado. */
  private async onPasswordAttempt(sessionId: string, password: string) {
    if (this.state.incoming?.sessionId !== sessionId) return;

    const now = Date.now();
    this.passwordFailures = this.passwordFailures.filter((t) => now - t < PASSWORD_LOCKOUT_WINDOW_MS);
    const blocked =
      !this.state.accessPassword ||
      this.passwordFailures.length >= PASSWORD_LOCKOUT_FAILURES ||
      this.passwordAttempts >= PASSWORD_ATTEMPTS_PER_REQUEST;

    // Conta antes de verificar para que tentativas simultâneas não passem do limite.
    if (!blocked) this.passwordAttempts++;
    const ok = !blocked && (await accessPasswordVerify(password).catch(() => false));
    if (this.state.incoming?.sessionId !== sessionId) return;

    if (ok) {
      this.acceptIncoming('password');
      return;
    }
    if (!blocked) this.passwordFailures.push(now);
    const attemptsLeft = blocked ? 0 : PASSWORD_ATTEMPTS_PER_REQUEST - this.passwordAttempts;
    this.signaling?.send({ type: 'session_password_rejected', sessionId, attemptsLeft });
  }

  private sendSignal(payload: SignalPayload) {
    const session = this.state.session;
    if (!session) return;
    this.signaling?.send({ type: 'signal', sessionId: session.info.sessionId, token: session.info.token, payload });
  }

  /* ---------------- ciclo de vida da sessão ---------------- */

  private async onSessionStarted(info: SessionInfo) {
    if (info.role === 'host') {
      await this.startHostSession(info);
      return;
    }
    await this.teardownSession();
    this.update({
      outgoing: null,
      incoming: null,
      notice: null,
      session: {
        info,
        peerState: 'connecting',
        inputOpen: false,
        remoteStream: null,
        screen: null,
        rttMs: null,
        link: null,
        settings: DEFAULT_SESSION_SETTINGS,
        volume: 1,
        remoteCursor: null,
        mouseBlocked: false,
        inputBlocked: false,
        remoteFiles: null,
        transfer: null,
      },
    });

    try {
      if (info.access === 'control' && (info.clipboard || info.fileTransfer)) {
        this.fileTransfer = new SessionTransfer({
          send: (message) => this.sendInput(message),
          channel: () => this.viewerPeer?.transferChannel() ?? null,
          setView: (view) => this.patchSession({ remoteFiles: view.remoteFiles, transfer: view.progress }),
          notify: (kind, text) => this.update({ notice: { kind, text } }),
          forwardPasteKey: () => {
            this.sendInput({ type: 'keyboard', code: 'KeyV', key: 'v', action: 'down' });
            this.sendInput({ type: 'keyboard', code: 'KeyV', key: 'v', action: 'up' });
          },
          clipboard: info.clipboard,
          fileTransfer: info.fileTransfer,
        });
      }
      this.viewerPeer = new ViewerPeer({
        iceServers: info.iceServers,
        relayOnly: FORCE_RELAY,
        onIceCandidate: (candidate) => this.sendSignal({ kind: 'ice', ...candidate }),
        onState: (peerState) => this.onPeerState(peerState),
        onTrack: (remoteStream) => this.patchSession({ remoteStream }),
        onInputOpen: (inputOpen) => {
          this.patchSession({ inputOpen });
          if (inputOpen) {
            this.sendSessionSettings();
            this.startPing();
          } else {
            this.stopPing();
          }
        },
        onInputMessage: (message) => {
          if (this.fileTransfer?.onHostMessage(message)) return;
          this.onHostInputMessage(message);
        },
        onTransferOpen: () => this.fileTransfer?.transferOpened(),
        onTransferBytes: (bytes) => this.fileTransfer?.onBytes(bytes),
      });
    } catch (err) {
      console.error('falha ao iniciar a sessão', err);
      this.signaling?.send({ type: 'session_end', sessionId: info.sessionId });
      await this.teardownSession();
      this.update({ session: null, notice: { kind: 'error', text: `Não foi possível iniciar a sessão: ${String(err)}` } });
    }
  }

  /** Host: mais uma pessoa entrou. Quem já estava conectado continua. */
  private async startHostSession(info: SessionInfo) {
    const request = this.state.incoming?.sessionId === info.sessionId ? this.state.incoming : null;
    if (request) this.update({ incoming: null });
    // Com o serviço, o agente inicia a transmissão e manda a lista de conexões.
    if (this.agentMode) return;
    this.hostInfos.set(info.sessionId, info);
    // Servidor antigo não repete no `session_started` o que mandou no pedido.
    const connection: HostConnection = {
      sessionId: info.sessionId,
      peerId: info.peerId,
      peerName: info.peerName,
      user: info.viewerUser ?? request?.viewerUser ?? null,
      organization: info.organization ?? request?.organization ?? null,
      access: info.access,
      state: 'connecting',
    };
    this.update({ hostSessions: [...this.state.hostSessions, connection] });
    try {
      const offer = await hostStart(
        info.sessionId,
        info.iceServers,
        FORCE_RELAY,
        info.access === 'control',
        info.clipboard,
        info.fileTransfer,
      );
      this.sendHostSignal(info.sessionId, { kind: 'offer', sdp: offer });
    } catch (err) {
      console.error('falha ao iniciar a sessão', err);
      this.endHostSessions([info.sessionId]);
      this.update({ notice: { kind: 'error', text: `Não foi possível iniciar a sessão: ${String(err)}` } });
    }
  }

  private sendHostSignal(sessionId: string, payload: SignalPayload) {
    const info = this.hostInfos.get(sessionId);
    if (info) this.signaling?.send({ type: 'signal', sessionId, token: info.token, payload });
  }

  /** `failed` é definitivo no WebRTC: só esta pessoa sai, as outras continuam. */
  private onHostPeerState(sessionId: string, state: PeerState) {
    this.update({
      hostSessions: this.state.hostSessions.map((s) => (s.sessionId === sessionId ? { ...s, state } : s)),
    });
    if (state === 'failed') this.endHostSessions([sessionId]);
  }

  private async onSignal(sessionId: string, payload: SignalPayload) {
    const hosting = this.hostInfos.has(sessionId);
    if (!hosting && this.state.session?.info.sessionId !== sessionId) return;

    try {
      if (hosting) {
        if (payload.kind === 'answer') await hostSetAnswer(sessionId, payload.sdp);
        else if (payload.kind === 'ice') await hostAddIceCandidate(sessionId, payload);
      } else if (this.viewerPeer) {
        if (payload.kind === 'offer') {
          const answer = await this.viewerPeer.acceptOffer(payload.sdp);
          this.sendSignal({ kind: 'answer', sdp: answer });
        } else if (payload.kind === 'ice') {
          await this.viewerPeer.addIceCandidate(payload);
        }
      }
    } catch (err) {
      console.error('erro no signaling', err);
      this.update({ notice: { kind: 'error', text: `Erro na negociação da conexão: ${String(err)}` } });
    }
  }

  private async listenHostEvents() {
    const forHosted = <T extends { sessionId: string }>(fn: (event: T) => void) => (event: T) => {
      if (this.hostInfos.has(event.sessionId)) fn(event);
    };
    this.hostUnlisten = await Promise.all([
      onHostIce(
        forHosted((event) => {
          this.sendHostSignal(event.sessionId, {
            kind: 'ice',
            candidate: event.candidate,
            sdpMid: event.sdpMid,
            sdpMLineIndex: event.sdpMLineIndex,
          });
        }),
      ),
      onHostState(forHosted((event) => this.onHostPeerState(event.sessionId, event.state))),
    ]);
  }

  private sendSessionSettings() {
    const settings = this.state.session?.settings;
    if (settings) this.viewerPeer?.sendInput({ type: 'session_settings', settings });
  }

  private onHostInputMessage(message: HostInputMessage) {
    switch (message.type) {
      case 'pong':
        this.patchSession({ rttMs: Math.max(0, Date.now() - message.ts) });
        return;
      case 'screen_info':
        this.patchSession({
          screen: {
            frameWidth: message.frameWidth,
            frameHeight: message.frameHeight,
            monitorWidth: message.monitorWidth,
            monitorHeight: message.monitorHeight,
            monitors: message.monitors ?? [],
          },
        });
        return;
      case 'cursor_shape':
        this.cursorImages.set(message.id, cursorImage(message));
        return;
      case 'cursor': {
        const image = message.shapeId === undefined ? null : (this.cursorImages.get(message.shapeId) ?? null);
        const look = this.state.session?.remoteCursor;
        if (look?.visible !== message.visible || look.image !== image) {
          this.patchSession({ remoteCursor: { visible: message.visible, image } });
        }
        this.moveRemoteCursor(message.visible ? { x: message.x, y: message.y } : null);
        return;
      }
      case 'mouse_blocked':
        this.patchSession({ mouseBlocked: message.blocked });
        return;
      case 'input_block': {
        const session = this.state.session;
        // Estava bloqueado e deixou de estar sem pedido daqui: a pessoa do Host liberou (Ctrl+Alt+Del).
        if (!message.active && session?.inputBlocked && session.settings.blockLocalInput) {
          this.update({
            session: { ...session, settings: { ...session.settings, blockLocalInput: false }, inputBlocked: false },
            notice: { kind: 'info', text: 'A pessoa do outro lado liberou o mouse e o teclado dela.' },
          });
          this.sendSessionSettings();
          return;
        }
        this.patchSession({ inputBlocked: message.active });
        return;
      }
    }
  }

  private moveRemoteCursor(position: { x: number; y: number } | null) {
    this.cursorPosition = position;
    for (const listener of this.cursorListeners) listener(position);
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.viewerPeer?.sendInput({ type: 'ping', ts: Date.now() });
      void this.viewerPeer?.linkSummary().then((link) => {
        if (link) this.patchSession({ link });
      });
    }, PING_INTERVAL_MS);
  }

  private stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private async teardownSession() {
    this.stopPing();
    this.fileTransfer?.close();
    this.fileTransfer = null;
    this.cursorImages.clear();
    this.moveRemoteCursor(null);
    if (INSIDE_APP) void clipboardRelease().catch(() => undefined);
    if (this.viewerPeer) {
      this.viewerPeer.close();
      this.viewerPeer = null;
    }
  }

  private patchSession(patch: Partial<ActiveSession>) {
    if (!this.state.session) return;
    this.update({ session: { ...this.state.session, ...patch } });
  }

  private update(patch: Partial<AppState>) {
    const hostSessions = this.state.hostSessions;
    this.state = { ...this.state, ...patch };
    if (INSIDE_APP && this.state.hostSessions !== hostSessions) {
      void traySetConnections(this.state.hostSessions).catch((err) => console.warn('falha ao atualizar a bandeja', err));
    }
    for (const listener of this.listeners) listener();
  }
}
